import { useState, useEffect } from 'react';
import confetti from 'canvas-confetti';
import { useNavigate } from 'react-router-dom';
import { MatchState, Team, Rally } from '@/types';
import { exportMatchReport, exportTournamentData } from '@/lib/export';
import { getTournamentData, getPlayerOfMatch, PlayerOfMatchWinner, getTournament, lockMatch } from '@/lib/supabase';
import BestCatcherSelectionModal from '@/components/modals/BestCatcherSelectionModal';
import AppHeader from '@/components/AppHeader';
import { TeamLogo } from '@/components/TeamLogo';
import { getSetScoreColor } from '@/lib/setScoreColor';

interface MatchCompleteProps {
  matchState: MatchState;
  teams: Team[];
  onNewMatch: () => void;
  onUpdateBestCatcher: (playerIds: string[]) => Promise<void>;
  onSignOut: () => void;
  readOnly?: boolean;
}

export default function MatchComplete({ matchState, teams, onNewMatch, onUpdateBestCatcher, onSignOut, readOnly = false }: MatchCompleteProps) {
  const navigate = useNavigate();
  const [showFullHistory, setShowFullHistory] = useState(false);
  const [exporting, setExporting] = useState<'match' | 'tournament' | null>(null);
  const [showBestCatcher, setShowBestCatcher] = useState(false);
  const [showEndMatchConfirm, setShowEndMatchConfirm] = useState(false);
  const [playerOfMatch, setPlayerOfMatch] = useState<PlayerOfMatchWinner[]>([]);
  const [tournamentName, setTournamentName] = useState<string | null>(null);
  const [bestCatcherSaving, setBestCatcherSaving] = useState(false);
  const [endingMatch, setEndingMatch] = useState(false);
  const [showThankYou, setShowThankYou] = useState(!readOnly);

  // Fire confetti for scorer when match completes
  useEffect(() => {
    if (readOnly) return;
    const winnerColor = matchState.match?.winnerId === matchState.teamA?.id
      ? matchState.teamA?.color
      : matchState.teamB?.color;
    const colors = winnerColor ? [winnerColor, '#ffffff', '#FFD700'] : ['#2563EB', '#ffffff', '#FFD700'];

    confetti({ particleCount: 120, spread: 80, origin: { y: 0.55 }, colors });
    const t1 = setTimeout(() => confetti({ particleCount: 60, angle: 60, spread: 55, origin: { x: 0, y: 0.6 }, colors }), 250);
    const t2 = setTimeout(() => confetti({ particleCount: 60, angle: 120, spread: 55, origin: { x: 1, y: 0.6 }, colors }), 400);
    const t3 = setTimeout(() => confetti({ particleCount: 80, spread: 100, origin: { y: 0.4 }, colors, scalar: 0.8 }), 700);

    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-open Best Catcher selection if not yet saved after match completion
  useEffect(() => {
    // Only auto-open if:
    // 1. Match is completed (match-won status)
    // 2. Best Catcher not yet selected
    // 3. Match is not locked
    // 4. Not in read-only mode
    if (
      matchState.match?.status === 'match-won' &&
      matchState.bestCatcherIds.length === 0 &&
      !matchState.match.isLocked &&
      !readOnly
    ) {
      setShowBestCatcher(true);
    }
  }, [matchState.match?.status, matchState.bestCatcherIds.length, matchState.match?.isLocked, readOnly]);

  // Load tournament name if match belongs to a tournament
  useEffect(() => {
    const tid = matchState.match?.tournamentId;
    if (!tid) { setTournamentName(null); return; }
    getTournament(tid)
      .then(t => setTournamentName(t.name))
      .catch(() => setTournamentName(null));
  }, [matchState.match?.tournamentId]);

  useEffect(() => {
    const matchId = matchState.match?.id;
    const status = matchState.match?.status;
    console.log('[MatchComplete] useEffect triggered: matchId=', matchId, 'status=', status);
    if (!matchId || status !== 'match-won') {
      console.log('[MatchComplete] Skipping getPlayerOfMatch: missing matchId or status !== match-won');
      return;
    }
    console.log('[MatchComplete] Calling getPlayerOfMatch for matchId=', matchId);
    getPlayerOfMatch(matchId)
      .then(winners => {
        console.log('[MatchComplete] getPlayerOfMatch returned', winners.length, 'winner(s):', winners);
        setPlayerOfMatch(winners);
      })
      .catch(err => console.error('[MatchComplete] getPlayerOfMatch failed:', err));
  }, [matchState.match?.id, matchState.match?.status]);

  const handleExportMatch = async () => {
    setExporting('match');
    try {
      await exportMatchReport(matchState, teams);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExporting(null);
    }
  };

  const handleExportTournament = async () => {
    setExporting('tournament');
    try {
      const data = await getTournamentData();
      exportTournamentData(data, teams);
    } catch (err) {
      console.error('Tournament export failed:', err);
    } finally {
      setExporting(null);
    }
  };

  const handleBestCatcherSave = async (playerIds: string[]) => {
    setBestCatcherSaving(true);
    try {
      await onUpdateBestCatcher(playerIds);
      // Recalculate Player of Match after best catcher update
      if (matchState.match?.id) {
        console.log('[MatchComplete] Best Catcher saved, recalculating Player of Match for matchId=', matchState.match.id);
        const winners = await getPlayerOfMatch(matchState.match.id);
        console.log('[MatchComplete] Recalculated Player of Match:', winners);
        setPlayerOfMatch(winners);
      }
      setShowBestCatcher(false);
      // Show End Match confirmation
      setShowEndMatchConfirm(true);
    } catch (err) {
      console.error('Failed to save Best Catcher:', err);
    } finally {
      setBestCatcherSaving(false);
    }
  };

  const handleEndMatch = async () => {
    if (!matchState.match?.id) return;
    setEndingMatch(true);
    try {
      await lockMatch(matchState.match.id);
      setShowEndMatchConfirm(false);
      onNewMatch();
    } catch (err) {
      console.error('Failed to end match:', err);
    } finally {
      setEndingMatch(false);
    }
  };

  if (!matchState.match || !matchState.teamA || !matchState.teamB) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-text-secondary">Loading...</p>
      </div>
    );
  }

  const winner = matchState.match.winnerId === matchState.teamA.id ? matchState.teamA : matchState.teamB;
  const matchTypeLabel = matchState.match.matchType.charAt(0).toUpperCase() + matchState.match.matchType.slice(1);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header with tournament context */}
      <AppHeader
        onSignOut={onSignOut}
        title={tournamentName || `Match #${matchState.match.matchNumber}`}
        subtitle={tournamentName ? `${matchTypeLabel} Match #${matchState.match.matchNumber}` : matchState.match.date}
        tournamentId={matchState.match.tournamentId ?? undefined}
        tournamentName={tournamentName ?? undefined}
      />

      <div className="flex-1 overflow-y-auto px-4 py-6 max-w-md mx-auto w-full">
        {/* Thank you banner — scorer only */}
        {showThankYou && (
          <div className="relative mb-5 rounded-xl border border-border bg-surface px-4 py-3 flex items-center gap-3">
            <span className="text-2xl">🎉</span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-text-primary">Match complete — great job!</p>
              <p className="text-xs text-text-muted">Thanks for scoring. Your work keeps the game running.</p>
            </div>
            <button
              onClick={() => setShowThankYou(false)}
              className="flex-shrink-0 p-1 rounded text-text-muted hover:text-text-primary transition-colors"
              aria-label="Dismiss"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="M18 6 6 18M6 6l12 12"/>
              </svg>
            </button>
          </div>
        )}

        {/* Lock Status Badge */}
        {matchState.match.isLocked && (
          <div className="mb-4 flex items-center justify-center gap-2 py-2 px-4 rounded-lg bg-surface-raised border border-border">
            <span className="text-lg">🔒</span>
            <span className="text-xs font-bold text-text-muted uppercase">Match Locked</span>
          </div>
        )}

        {/* Match Winner */}
        <div
          className="rounded-xl p-6 mb-5 text-center"
          style={{ backgroundColor: `${winner.color}15`, borderColor: winner.color, borderWidth: 2 }}
        >
          <div className="flex items-center justify-center gap-2 mb-3">
            <span className="text-2xl">🏆</span>
            <p className="text-sm text-text-muted uppercase font-bold tracking-wider">Match Won</p>
          </div>
          <div className="flex justify-center mb-4">
            <TeamLogo team={winner} size="2xl" />
          </div>
          <p className="text-2xl font-bold" style={{ color: winner.color }}>
            {winner.name.toUpperCase()}
          </p>
          <div className="flex items-center justify-center gap-3 mt-3">
            <span className="text-sm font-bold" style={{ color: matchState.teamA?.color }}>
              {matchState.teamA?.setsWon}
            </span>
            <span className="text-text-muted">-</span>
            <span className="text-sm font-bold" style={{ color: matchState.teamB?.color }}>
              {matchState.teamB?.setsWon}
            </span>
          </div>
        </div>

        {/* Best Catcher Section - Editable */}
        <div className="card mb-5 border-2 border-success/30 bg-success/5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="text-lg">🧤</span>
              <h2 className="text-xs font-bold text-success uppercase tracking-wider">Best Catcher</h2>
            </div>
            {!readOnly && !matchState.match.isLocked && (
              <button
                onClick={() => setShowBestCatcher(true)}
                className="text-xs font-semibold text-success hover:underline"
              >
                {matchState.bestCatcherIds.length > 0 ? 'Edit' : 'Select'}
              </button>
            )}
          </div>

          {matchState.bestCatcherIds.length > 0 ? (
            <div className="space-y-2">
              {matchState.bestCatcherIds.map(id => {
                const player = matchState.teamA?.players.find(p => p.id === id) || matchState.teamB?.players.find(p => p.id === id);
                const team = matchState.teamA?.players.find(p => p.id === id) ? matchState.teamA : matchState.teamB;
                if (!player || !team) return null;
                return (
                  <div key={id} className="flex items-center justify-between py-1">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: team.color }} />
                      <span className="text-sm font-semibold" style={{ color: team.color }}>{player.name}</span>
                    </div>
                    <span className="text-xs text-text-muted">{team.shortName}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-text-muted text-center py-4">
              No Best Catcher selected yet.
            </p>
          )}

          {!readOnly && !matchState.match.isLocked && (
            <button
              onClick={() => setShowBestCatcher(true)}
              disabled={bestCatcherSaving}
              className="w-full mt-4 py-3 px-4 rounded-lg font-bold text-sm bg-success text-white transition-all active:scale-[0.98] disabled:opacity-50"
            >
              {bestCatcherSaving ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  SAVING...
                </span>
              ) : matchState.bestCatcherIds.length > 0 ? (
                'UPDATE BEST CATCHER'
              ) : (
                'SELECT BEST CATCHER'
              )}
            </button>
          )}
        </div>

        {/* Player Of The Match */}
        {playerOfMatch.length > 0 ? (
          <div className="card mb-5 border-2 border-warning/30 bg-warning/5">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-lg">🏆</span>
              <h2 className="text-xs font-bold text-warning uppercase tracking-wider">Player Of The Match</h2>
            </div>
            <div className="space-y-3">
              {playerOfMatch.map(p => {
                const team = matchState.teamA?.id === p.teamId ? matchState.teamA : matchState.teamB;
                return (
                <div key={p.playerId} className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-bold" style={{ color: team?.color }}>{p.playerName}</p>
                    <p className="text-xs text-text-muted">{p.teamName}</p>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-1 justify-end">
                      {'⭐'.repeat(Math.min(p.totalStars, 1))}
                      {p.totalStars > 10 && <span className="text-xs text-warning font-bold">×{p.totalStars}</span>}
                    </div>
                    <p className="text-[10px] text-text-muted mt-0.5">
                      {[p.throwerStars > 0 && `${p.throwerStars}🎯`, p.acerStars > 0 && `${p.acerStars}⚡`, p.catcherStars > 0 && `${p.catcherStars}🧤`].filter(Boolean).join(' ')}
                    </p>
                  </div>
                </div>
              );})}
            </div>
          </div>
        ) : matchState.match?.status === 'match-won' && (
          <div className="card mb-5 border border-border">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">🏆</span>
              <h2 className="text-xs font-bold text-text-muted uppercase tracking-wider">Player Of The Match</h2>
            </div>
            <p className="text-xs text-text-muted text-center py-2">No stars recorded yet. Select Best Catcher or score with point winners to calculate.</p>
          </div>
        )}

        {/* Set Scores */}
        <div className="card mb-5">
          <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-4">SET SCORES</h2>
          <div className="space-y-3">
            {matchState.sets.map((set, index) => {
              const { color: scoreColor } = getSetScoreColor(set, matchState.teamA, matchState.teamB);
              return (
                <div key={set.id} className="flex items-center justify-between py-2">
                  <span className="text-sm text-text-secondary">Set {index + 1}</span>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold w-6" style={{ color: matchState.teamA?.color }}>{matchState.teamA?.shortName}</span>
                      <span className="font-mono-nums text-xl font-bold" style={{ color: scoreColor ?? undefined }}>{set.scoreA}</span>
                    </div>
                    <span className="text-text-muted">—</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono-nums text-xl font-bold" style={{ color: scoreColor ?? undefined }}>{set.scoreB}</span>
                      <span className="text-sm font-semibold w-6" style={{ color: matchState.teamB?.color }}>{matchState.teamB?.shortName}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Match Stats */}
        <div className="card mb-5">
          <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-4">MATCH STATS</h2>
          <div className="space-y-2">
            <div className="flex items-center justify-between py-2 border-b border-border">
              <span className="text-text-secondary">Sets Played</span>
              <span className="font-mono-nums font-bold text-text-primary">{matchState.sets.length}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-border">
              <span className="text-text-secondary">Sets Won by {matchState.teamA.shortName}</span>
              <span className="font-mono-nums font-bold" style={{ color: matchState.teamA.color }}>{matchState.teamA.setsWon}</span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-text-secondary">Sets Won by {matchState.teamB.shortName}</span>
              <span className="font-mono-nums font-bold" style={{ color: matchState.teamB.color }}>{matchState.teamB.setsWon}</span>
            </div>
          </div>
        </div>

        {/* View Point History */}
        <button onClick={() => setShowFullHistory(!showFullHistory)} className="btn-secondary mb-4">
          {showFullHistory ? 'HIDE POINT HISTORY' : 'VIEW POINT HISTORY'}
        </button>

        {showFullHistory && (
          <div className="card mb-6 max-h-80 overflow-y-auto">
            {(() => {
              const matchId = matchState.match?.id;
              console.log('[PointHistory] Toggle state:', showFullHistory);
              console.log('[PointHistory] Match ID:', matchId);

              // Collect all rallies from all sets with running score
              const allRallyRows: {
                rallyNumber: number;
                setNumber: number;
                rally: Rally;
                scoreAfter: string;
              }[] = [];

              let globalRallyNumber = 0;
              for (let setIndex = 0; setIndex < matchState.sets.length; setIndex++) {
                const set = matchState.sets[setIndex];
                const rallies = set.rallies ?? [];
                // Rallies are stored descending (newest first) — reverse for chronological display
                const chronological = [...rallies].reverse();
                let runningScoreA = 0;
                let runningScoreB = 0;
                for (const rally of chronological) {
                  globalRallyNumber++;
                  if (rally.pointWonById === matchState.teamA?.id) runningScoreA++;
                  else if (rally.pointWonById === matchState.teamB?.id) runningScoreB++;
                  allRallyRows.push({
                    rallyNumber: globalRallyNumber,
                    setNumber: set.setNumber,
                    rally,
                    scoreAfter: `${runningScoreA}–${runningScoreB}`,
                  });
                }
              }

              console.log('[PointHistory] Rally count:', allRallyRows.length);

              if (allRallyRows.length === 0) {
                console.log('[PointHistory] No rallies found');
                return (
                  <div className="text-center py-8">
                    <p className="text-text-muted text-sm">No rally history available.</p>
                  </div>
                );
              }

              console.log('[PointHistory] Rendered');
              return (
                <div className="space-y-1">
                  {allRallyRows.map(({ rallyNumber, setNumber, rally, scoreAfter }) => {
                    const servingTeam = rally.servingTeamId === matchState.teamA?.id ? matchState.teamA : matchState.teamB;
                    const winningTeam = rally.pointWonById === matchState.teamA?.id ? matchState.teamA : matchState.teamB;
                    const server = servingTeam?.players.find(p => p.id === rally.serverId);
                    const allPlayers = [...(matchState.teamA?.players ?? []), ...(matchState.teamB?.players ?? [])];
                    const pointWinner = rally.pointWinnerId ? allPlayers.find(p => p.id === rally.pointWinnerId) : null;
                    return (
                      <div key={rally.id} className="flex items-center gap-2 px-3 py-2 text-xs border-b border-border last:border-b-0">
                        <span className="text-text-muted w-8">R{rallyNumber}</span>
                        <span className="text-text-muted w-6">S{setNumber}</span>
                        <span style={{ color: servingTeam?.color }} className="font-semibold w-8">{servingTeam?.shortName}</span>
                        <span className="w-16 truncate font-medium" style={{ color: servingTeam?.color }}>{server?.name}</span>
                        <span className="text-text-muted">→</span>
                        <span style={{ color: winningTeam?.color }} className="font-semibold w-8">{winningTeam?.shortName}</span>
                        <span className="text-text-muted uppercase w-8">{rally.lostReason}</span>
                        {pointWinner && (
                          <span className="w-16 truncate font-medium" style={{ color: winningTeam?.color }}>{pointWinner.name}</span>
                        )}
                        <span className="text-text-muted ml-auto">{scoreAfter}</span>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        )}

        {/* Export Buttons */}
        <div className="space-y-3 mb-6">
          <button onClick={handleExportMatch} disabled={exporting !== null} className="btn-primary bg-team-a text-white font-bold py-4 flex items-center justify-center gap-2 disabled:opacity-50">
            {exporting === 'match' ? (
              <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />EXPORTING...</>
            ) : 'EXPORT MATCH REPORT'}
          </button>
          <button onClick={handleExportTournament} disabled={exporting !== null} className="btn-primary bg-team-b text-white font-bold py-4 flex items-center justify-center gap-2 disabled:opacity-50">
            {exporting === 'tournament' ? (
              <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />EXPORTING...</>
            ) : 'EXPORT TOURNAMENT DATA'}
          </button>
        </div>

        {!readOnly && (
          <button onClick={onNewMatch} className="btn-primary bg-team-a text-white font-bold py-4">NEW MATCH</button>
        )}
        {matchState.match?.tournamentId && (
          <button
            onClick={() => navigate(`/tournament/${matchState.match!.tournamentId}`)}
            className="w-full py-4 rounded-xl font-bold text-sm bg-surface border border-border text-text-secondary active:scale-[0.98] transition-all"
          >
            BACK TO TOURNAMENT
          </button>
        )}
      </div>

      {showBestCatcher && matchState.teamA && matchState.teamB && (
        <BestCatcherSelectionModal
          teamAName={matchState.teamA.name}
          teamBName={matchState.teamB.name}
          teamAColor={matchState.teamA.color}
          teamBColor={matchState.teamB.color}
          playersTeamA={matchState.teamA.players}
          playersTeamB={matchState.teamB.players}
          initiallySelectedIds={matchState.bestCatcherIds}
          onSave={handleBestCatcherSave}
          onCancel={() => setShowBestCatcher(false)}
          isFinal={true}
        />
      )}

      {/* End Match Confirmation Dialog */}
      {showEndMatchConfirm && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-surface rounded-xl p-6 max-w-sm w-full shadow-2xl">
            <div className="text-center mb-4">
              <span className="text-4xl">✓</span>
            </div>
            <h3 className="text-lg font-bold text-text-primary mb-2 text-center">
              Best Catcher Saved Successfully!
            </h3>
            <p className="text-sm text-text-secondary mb-6 text-center">
              Do you want to End Match? This will lock the match and prevent further modifications.
            </p>
            <div className="space-y-3">
              <button
                onClick={handleEndMatch}
                disabled={endingMatch}
                className="w-full py-3 px-4 rounded-lg font-bold text-sm bg-success text-white transition-all active:scale-[0.98] disabled:opacity-50"
              >
                {endingMatch ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ENDING MATCH...
                  </span>
                ) : (
                  'END MATCH'
                )}
              </button>
              <button
                onClick={() => setShowEndMatchConfirm(false)}
                disabled={endingMatch}
                className="w-full py-3 px-4 rounded-lg font-semibold text-sm bg-surface border border-border text-text-primary transition-all active:scale-[0.98] hover:bg-surface-raised disabled:opacity-50"
              >
                CONTINUE VIEWING
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
